/**
 * Workspace Settings > Google Drive: blank-Doc template copied on Publish.
 */
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'

type PublishTemplateState = {
  templateId: string | null
  name: string | null
  webViewLink: string | null
  warning?: string
}

export function DrivePublishTemplateCard({
  organizationId,
  canEdit,
}: {
  organizationId: string
  canEdit: boolean
}) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [input, setInput] = useState('')
  const [current, setCurrent] = useState<PublishTemplateState | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void fetchWithAuth(
      `/api/google-drive/publish-template?organizationId=${encodeURIComponent(organizationId)}`,
    )
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as PublishTemplateState & { error?: string }
        if (cancelled) return
        if (!res.ok) {
          setCurrent(null)
          return
        }
        setCurrent({
          templateId: body.templateId || null,
          name: body.name || null,
          webViewLink: body.webViewLink || null,
          warning: body.warning,
        })
        setInput('')
      })
      .catch(() => {
        if (!cancelled) setCurrent(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [organizationId])

  const save = async () => {
    const templateUrl = input.trim()
    if (!templateUrl) {
      toast.error('Paste a Google Doc URL or file ID')
      return
    }
    setSaving(true)
    try {
      const res = await fetchWithAuth('/api/google-drive/publish-template', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId, templateUrl }),
      })
      const body = (await res.json().catch(() => ({}))) as PublishTemplateState & {
        error?: string
        detail?: string
      }
      if (!res.ok) {
        toast.error(typeof body.error === 'string' ? body.error : 'Could not save publish template')
        return
      }
      setCurrent({
        templateId: body.templateId || null,
        name: body.name || null,
        webViewLink: body.webViewLink || null,
      })
      setInput('')
      toast.success('Publish template saved')
    } catch {
      toast.error('Could not save publish template')
    } finally {
      setSaving(false)
    }
  }

  const clear = async () => {
    setClearing(true)
    try {
      const res = await fetchWithAuth(
        `/api/google-drive/publish-template?organizationId=${encodeURIComponent(organizationId)}`,
        { method: 'DELETE' },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        toast.error(typeof body.error === 'string' ? body.error : 'Could not clear template')
        return
      }
      setCurrent({ templateId: null, name: null, webViewLink: null })
      setInput('')
      toast.success('Publish template cleared')
    } catch {
      toast.error('Could not clear template')
    } finally {
      setClearing(false)
    }
  }

  return (
    <Card className="border-0 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-base">Publish template</CardTitle>
        <CardDescription>
          New Google Docs from Publish to Drive are copied from this blank Doc so they keep its heading styles.
          Leave empty to use the Google API default (older heading sizes).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading template...
          </div>
        ) : (
          <>
            {current?.templateId ? (
              <p className="text-sm">
                {current.webViewLink ? (
                  <a
                    href={current.webViewLink}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium underline-offset-2 hover:underline"
                    data-testid={testIds.workspaceSettings.googleDrivePublishTemplateLink}
                  >
                    {current.name || 'Open template'}
                  </a>
                ) : (
                  <span className="font-medium">{current.name || current.templateId}</span>
                )}
                {current.warning ? (
                  <span className="mt-1 block text-xs text-muted-foreground">{current.warning}</span>
                ) : null}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No publish template set.</p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="drive-publish-template-url" className="text-xs">
                Google Doc URL
              </Label>
              <Input
                id="drive-publish-template-url"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="https://docs.google.com/document/d/..."
                disabled={!canEdit || saving}
                data-testid={testIds.workspaceSettings.googleDrivePublishTemplateInput}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!canEdit || saving || !input.trim()}
                onClick={() => void save()}
                data-testid={testIds.workspaceSettings.googleDrivePublishTemplateSave}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save template'}
              </Button>
              {current?.templateId ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canEdit || clearing}
                  onClick={() => void clear()}
                  data-testid={testIds.workspaceSettings.googleDrivePublishTemplateClear}
                >
                  {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Clear'}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
